/**
 * Session identity and the customer OTP exchange.
 *
 * Authority: PRD §4.1–§4.2, S3, S10; scope-5ht (session resolution);
 * scope-9xn (ACS email OTP); scope-caw.1 (staff Entra ID sign-in).
 *
 * Two subject types, two entirely separate paths. **A customer OTP session can
 * never be upgraded to a staff session** — staff authenticate through Entra ID
 * and never through this OTP flow.
 *
 * A handler receives its engagement identity from the resolved session, never
 * from request input; reading an engagement id out of a request outside
 * `packages/api-auth` is a lint failure (scope-5ht), which is why no request
 * DTO in this package carries one.
 */
import { z } from 'zod';
import { EmailSchema, TimestampSchema, UuidSchema } from './primitives.js';
export const SUBJECT_TYPES = ['customer', 'staff'];
export const SubjectTypeSchema = z.enum(SUBJECT_TYPES);
/**
 * PRD §4.2. Roles live in Entra ID (scope-kay) and arrive as token claims; this
 * is the vocabulary, not the store.
 */
export const STAFF_ROLES = ['consultant', 'admin', 'approver'];
export const StaffRoleSchema = z.enum(STAFF_ROLES);
/**
 * A principal holds a *set* of roles, not one (IMPL-STAFF-003 §3.2). The three
 * are orthogonal capabilities rather than a ladder: `approver` is not "admin
 * plus", and an admin does not approve commercially by virtue of being an
 * admin. Collapsing them to a single most-privileged winner is what made
 * separation of duties unexpressible.
 *
 * Sorted and deduplicated on construction so two principals holding the same
 * capabilities compare equal, and so a serialised session is stable.
 */
/**
 * The delivery side of the matrix: the people who run engagements.
 *
 * Named, rather than spelled `[...STAFF_ROLES]`, because those two used to be
 * the same list and are not any more. `approver` is a commercial sign-off
 * capability, not a seat on the delivery floor — an approver-only principal
 * has no business claiming a lead or issuing a participant invitation. Every
 * handler that meant "any staff member" meant this (IMPL-STAFF-003 §3.2).
 */
export const DELIVERY_ROLES = ['consultant', 'admin'];
/** Commercial sign-off, and nothing else (§3.3). */
export const APPROVAL_ROLES = ['approver'];
export const StaffRoleSetSchema = z
    .array(StaffRoleSchema)
    .nonempty()
    .transform((roles) => [...new Set(roles)].sort());
/**
 * A customer session is scoped to exactly one engagement — one person, one
 * engagement (DM-001 §3, `otp_sessions`). There is no customer session that
 * spans two, so there is no code path that could confuse them.
 */
export const CustomerSessionSchema = z.object({
    subject_type: z.literal('customer'),
    engagement_id: UuidSchema,
    email: EmailSchema,
    expires_at: TimestampSchema,
});
export const StaffSessionSchema = z.object({
    subject_type: z.literal('staff'),
    email: EmailSchema,
    roles: StaffRoleSetSchema,
    expires_at: TimestampSchema,
});
export const SessionSchema = z.discriminatedUnion('subject_type', [
    CustomerSessionSchema,
    StaffSessionSchema,
]);
/**
 * Idle and absolute session lifetimes, in seconds (scope-9xn).
 *
 * The customer window is deliberately long: a questionnaire is a long sitting,
 * and a staff-grade timeout causes data loss and drives people to draft answers
 * in a text file, which is worse for the data than the longer session is.
 */
export const SESSION_LIFETIMES_SECONDS = {
    customer: { idle: 2 * 60 * 60, absolute: 24 * 60 * 60 },
    staff: { idle: 30 * 60, absolute: 8 * 60 * 60 },
};
export const OtpSendRequestSchema = z.object({ email: EmailSchema });
/**
 * The response is identical in body, status **and timing** whether or not the
 * address is known (scope-9xn). It therefore says only that a code may now be
 * entered — never "sent", never "no account found", and it carries nothing that
 * identifies an engagement or an organisation.
 *
 * `RESEND_AVAILABLE` is the ambiguous-send outcome: the challenge row exists,
 * the mail may or may not have gone out, and the client must not retry. The
 * user presses resend, which supersedes the prior challenge.
 */
export const OTP_SEND_OUTCOMES = ['CHALLENGE_ISSUED', 'RESEND_AVAILABLE'];
export const OtpSendOutcomeSchema = z.enum(OTP_SEND_OUTCOMES);
export const OtpSendResponseSchema = z.object({
    outcome: OtpSendOutcomeSchema,
    /** Seconds until a resend is accepted. Not a statement that mail was sent. */
    retry_after_seconds: z.int().min(0),
});
export const OtpVerifyRequestSchema = z.object({
    email: EmailSchema,
    code: z.string().regex(/^\d{6}$/),
});
/**
 * Verification succeeded and a session cookie has been set. The session itself
 * is never in the body — it is an HttpOnly cookie, and a body copy would be
 * readable by script and defeat the point.
 */
/**
 * IMPL-ENGAGE-002 §8.2. `selection_required` is the person tier's fork.
 *
 * Sessions and OTP challenges are both minted bound to one engagement, so
 * there is a gap between "verified" and "chosen". When exactly one engagement
 * is reachable the response is what it always was and the client goes
 * straight in. When several are, the cookie carries a short-lived SELECTION
 * token instead of a session, and the client must choose.
 *
 * **The shape does not change between the two cases** — same fields, same
 * status — so nothing about the response discloses how many engagements an
 * address reaches to anyone who has not already verified it. The flag is only
 * meaningful post-verification, which is where §2.1 permits disclosure.
 */
export const OtpVerifyResponseSchema = z.object({
    subject_type: SubjectTypeSchema,
    expires_at: TimestampSchema,
    selection_required: z.boolean().default(false),
});
//# sourceMappingURL=auth.js.map